import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import { api, parseApiError } from '../lib/api';
import { cn } from '../lib/cn';
import { Card } from '../components/Card';
import { HelperCard } from '../components/HelperCard';
import { isAdminRole } from '../lib/roles';
import { isOrgManagerOfAny, managedOrgIds } from '../lib/orgAccess';
import { useEffectiveMe } from '../lib/viewAs';
import { usePageActions } from '../lib/pageActions';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { Swords, CheckCircle2 } from 'lucide-react';

function ErrorMsg({ msg }: { msg: string }) {
  return msg ? <p className="mt-2 text-xs text-red-400">{msg}</p> : null;
}

export function AdminMatchCreate() {
  const [searchParams] = useSearchParams();
  const paramCompId = searchParams.get('compId') ?? '';

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  // Org-admin de-gate: view-as-adjusted `me` (Sidebar.tsx / lib/viewAs.ts's
  // useEffectiveMe) — a founder previewing "Org admin (scoped)" sees exactly
  // this bypass fire for their simulated org; identity no-op when no view-as
  // is active, so real (non-previewing) behaviour is unchanged.
  const effectiveMe = useEffectiveMe(me);
  const { data: comps } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });

  // Scope the competition picker to the orgs this caller manages — same
  // managedOrgIds idiom TournamentSetup.tsx/DisciplinePage.tsx use.
  // managedOrgIds returns 'all' for founder/platform-admin (unfiltered,
  // unchanged). Backend (POST /admin/matches) re-checks per request
  // regardless — this is UX scoping only.
  const managedIds = managedOrgIds(effectiveMe);
  const compList = managedIds === 'all'
    ? (comps ?? [])
    : (comps ?? []).filter(c => managedIds.includes((c.orgId ?? '').replace(/^ORG#/, '')));

  const [compId, setCompId] = useState(paramCompId);
  const [clan1, setClan1] = useState('');
  const [clan2, setClan2] = useState('');
  const [streamerUrl, setStreamerUrl] = useState('');
  const [error, setError] = useState('');
  const [created, setCreated] = useState<{ matchId: string; clan1: string; clan2: string } | null>(null);

  const activeCompId = compId || compList[0]?.id || '';

  const { data: clans } = useQuery({
    queryKey: ['competitionClans', activeCompId],
    queryFn: () => api.competitionClans(activeCompId),
    enabled: !!activeCompId,
  });

  const mut = useMutation({
    mutationFn: () => api.admin.createMatch(activeCompId, clan1, clan2, undefined, streamerUrl.trim() || undefined),
    onSuccess: (data) => {
      setCreated(data);
      setClan1('');
      setClan2('');
      setStreamerUrl('');
      setError('');
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  // Org-admin de-gate: platform admin/founder (isAdminRole) OR an
  // ORG_ADMIN/ORG_OWNER of at least one org (isOrgManagerOfAny) — scoped to
  // their own comp(s) via compList above. Mirrors TournamentSetup.tsx/
  // ClanManagement.tsx/DisciplinePage.tsx's own gate exactly.
  const isOrgManager = isOrgManagerOfAny(effectiveMe);
  const hasMatchCreateAccess = !!me && (isAdminRole(effectiveMe?.role) || isOrgManager);

  // Global bottom action-bar registration — the SAME "Create Match" submit
  // as the button below (same disabled gate, same mut.mutate() call).
  // Hidden once `created` (the page swaps to a "Match Created" success
  // state with its own View/Create Another actions — nothing left to
  // submit). Gated on this page's own admin/org-manager access; hook must
  // sit before the early-return gate (Rules of Hooks).
  const invalidStreamerUrl = !!streamerUrl.trim() && !/^https:\/\/.+/.test(streamerUrl.trim());
  const canCreateMatch = !!clan1 && !!clan2 && !!activeCompId && !mut.isPending && !invalidStreamerUrl;
  const createMatchActions = useMemo(
    () => (hasMatchCreateAccess && !created ? [{
      id: 'admin-match-create',
      label: 'Create Match',
      icon: Swords,
      onClick: () => mut.mutate(),
      disabled: !canCreateMatch,
    }] : null),
    [hasMatchCreateAccess, created, canCreateMatch, mut.mutate],
  );
  usePageActions(createMatchActions);

  if (!hasMatchCreateAccess) {
    return <Card className="text-center"><p className="text-ink-secondary">Admin access required.</p></Card>;
  }

  const clanList = clans ?? [];
  const clan1Options = clanList.filter(c => c.name !== clan2);
  const clan2Options = clanList.filter(c => c.name !== clan1);

  return (
    <div>
      <div className={cn('mb-8 flex items-center gap-3', SETTLE)} style={settleDelay(0)}>
        <Swords className="h-7 w-7 text-ink-secondary" />
        <div>
          <h1 className="text-2xl font-bold text-ink-primary">Create Match</h1>
          <p className="text-xs text-ink-meta">Schedule a match between two clans (admin)</p>
        </div>
      </div>

      <div className={SETTLE} style={settleDelay(1)}>
      <HelperCard
        id="admin-match-create-intro"
        title="What happens when you create a match"
        intro="A match record is created and both clan leaders get a Match Card DM in Discord. They handle the veto from there — map picks flow through the Card buttons."
        points={[
          { role: 'admin', text: 'Pick a competition, then two enrolled clans. Both clans must be in the comp.' },
          { role: 'leader', text: 'Leaders see the match in their panel once it exists. They don\'t need to do anything to accept.' },
        ]}
        className="mb-6 max-w-lg"
      />
      </div>

      <div className={SETTLE} style={settleDelay(2)}>
      <Card className="max-w-lg">
        {created ? (
          <div className="space-y-4 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-green-400" />
            <div>
              <p className="text-lg font-bold text-ink-primary">Match Created</p>
              <p className="text-sm text-ink-secondary">
                {created.clan1} vs {created.clan2}
              </p>
            </div>
            <div className="flex gap-3 justify-center">
              <Link to={`/match/${encodeURIComponent(created.matchId)}?compId=${encodeURIComponent(activeCompId)}`}
                className={cn('btn-primary text-sm', MORPH)}>
                View Match
              </Link>
              <button onClick={() => setCreated(null)} className={cn('btn-ghost text-sm', MORPH)}>
                Create Another
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Competition */}
            <div>
              <label className="label">Competition</label>
              <select value={activeCompId} onChange={e => { setCompId(e.target.value); setClan1(''); setClan2(''); }} className="input w-full">
                {compList.filter(c => c.status !== 'CLOSED').map(c => (
                  <option key={c.id} value={c.id}>{c.name || c.id.replace('COMP#', '')} ({c.status})</option>
                ))}
              </select>
            </div>

            {/* Clan 1 */}
            <div>
              <label className="label">Clan 1</label>
              <select value={clan1} onChange={e => setClan1(e.target.value)} className="input w-full">
                <option value="">Select clan...</option>
                {clan1Options.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </div>

            {/* Clan 2 */}
            <div>
              <label className="label">Clan 2</label>
              <select value={clan2} onChange={e => setClan2(e.target.value)} className="input w-full">
                <option value="">Select clan...</option>
                {clan2Options.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </div>

            {/* Streamer link (T15) — shown as "Watch live" on the match card */}
            <div>
              <label className="label">Streamer link <span className="font-normal normal-case text-ink-meta">(optional)</span></label>
              <input
                value={streamerUrl}
                onChange={e => setStreamerUrl(e.target.value)}
                placeholder="https://twitch.tv/…"
                className="input w-full"
              />
            </div>

            <ErrorMsg msg={error} />

            <button
              onClick={() => mut.mutate()}
              disabled={!clan1 || !clan2 || !activeCompId || mut.isPending || (!!streamerUrl.trim() && !/^https:\/\/.+/.test(streamerUrl.trim()))}
              className={cn('btn-primary w-full', MORPH)}
            >
              {mut.isPending ? 'Creating...' : 'Create Match'}
            </button>
          </div>
        )}
      </Card>
      </div>
    </div>
  );
}
